import {
  runCancelable,
  CancelablePromise,
  once,
  chainCancelable,
} from "@/client/cancelable-promise";
import React from "react";

const sleepCancelable =
  (time: number) =>
  ({ signal }) =>
    new Promise((res, rej) => {
      let cleanup: (() => void) | undefined = undefined;
      const timeout = setTimeout(() => {
        if (cleanup) {
          cleanup();
        }
        res(undefined);
      }, time);
      cleanup = once(signal, "aborted", () => {
        clearTimeout(timeout);
        rej(signal.reason);
      });
    });

export const useDebounce = () => {
  const lastRef = React.useRef({
    promise: undefined as CancelablePromise<any> | undefined,
    unique: undefined as any,
  });
  return React.useMemo(() => {
    const cancel = (reason?: any) => {
      if (lastRef.current.promise) {
        lastRef.current.promise.cancel(reason);
      }
    };
    const run = <T extends ({ signal }) => Promise<any> | any>(
      time: number,
      action: T,
      unique?: any,
    ) => {
      if (unique !== undefined) {
        if (unique === lastRef.current.unique) {
          return;
        }
        lastRef.current.unique = unique;
      }
      cancel();
      const promise = runCancelable(async ({ signal }) => {
        try {
          if (signal.aborted) return;
          await chainCancelable(sleepCancelable(time), signal);
          if (signal.aborted) return;
          await chainCancelable(action, signal);
        } finally {
          lastRef.current.unique = undefined;
        }
      });
      promise.catch((_ex) => {});
      lastRef.current.promise = promise;
      return promise;
    };
    return Object.assign(run, { cancel });
  }, []);
};
